import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  SelectInput,
  BooleanInput,
  RichTextField,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  useListContext,
  useUnselectAll,
  Button,
  TopToolbar,
  ExportButton,
  ShowButton,
  DeleteButton,
  BulkDeleteButton,
  FunctionField,
  SearchInput,
  SimpleList,
} from 'react-admin';
import {
  Box,
  Typography,
  Rating,
  Chip,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  Theme,
} from '@mui/material';
import {
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Button as MuiButton } from '@mui/material';

const reviewFilters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="상품명 또는 작성자명 검색" />,
  <SelectInput
    key="rating"
    source="rating"
    choices={[
      { id: 1, name: '⭐ 1점' },
      { id: 2, name: '⭐⭐ 2점' },
      { id: 3, name: '⭐⭐⭐ 3점' },
      { id: 4, name: '⭐⭐⭐⭐ 4점' },
      { id: 5, name: '⭐⭐⭐⭐⭐ 5점' },
    ]}
  />,
  <SelectInput
    key="status"
    source="status"
    choices={[
      { id: 'APPROVED', name: '노출' },
      { id: 'HIDDEN', name: '미노출' },
    ]}
  />,
  <BooleanInput key="hasImages" source="hasImages" label="이미지 포함" />,
];

const RatingField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  return record ? <Rating value={record[source]} readOnly size="small" /> : null;
};

const StatusField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  if (!record) return null;

  const status = record[source];
  let icon, color;

  switch (status) {
    case 'APPROVED':
      icon = <ApprovedIcon sx={{ fontSize: 16 }} />;
      color = 'success';
      break;
    case 'HIDDEN':
      icon = <RejectedIcon sx={{ fontSize: 16 }} />;
      color = 'default';
      break;
    default:
      // 기본값은 APPROVED (노출)
      icon = <ApprovedIcon sx={{ fontSize: 16 }} />;
      color = 'success';
  }

  const labels: Record<string, string> = {
    APPROVED: '노출',
    HIDDEN: '미노출'
  };

  return (
    <Chip
      icon={icon}
      label={labels[status] || '노출'}
      color={color as any}
      size="small"
      variant="outlined"
    />
  );
};

const ReviewImages = () => {
  const record = useRecordContext();
  if (!record?.images || record.images.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {record.images.map((img: string, index: number) => (
        <Box
          key={index}
          component="img"
          src={img}
          alt={`리뷰 이미지 ${index + 1}`}
          sx={{
            width: 60,
            height: 60,
            objectFit: 'cover',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 }
          }}
          onClick={() => window.open(img, '_blank')}
        />
      ))}
    </Box>
  );
};

const BulkShowButton = () => {
  const { selectedIds } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('reviews');
  const dataProvider = useDataProvider();

  const handleShow = async () => {
    try {
      await dataProvider.updateMany('reviews', {
        ids: selectedIds,
        data: { status: 'APPROVED' }
      });
      notify('리뷰가 노출 상태로 변경되었습니다', { type: 'success' });
      unselectAll();
      refresh();
    } catch (error) {
      notify('리뷰 노출 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  return (
    <Button
      label="일괄 노출"
      onClick={handleShow}
    />
  );
};

const BulkHideButton = () => {
  const { selectedIds } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('reviews');
  const dataProvider = useDataProvider();

  const handleHide = async () => {
    try {
      await dataProvider.updateMany('reviews', {
        ids: selectedIds,
        data: { status: 'HIDDEN' }
      });
      notify('리뷰가 미노출 상태로 변경되었습니다', { type: 'info' });
      unselectAll();
      refresh();
    } catch (error) {
      notify('리뷰 숨김 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  return (
    <Button
      label="일괄 미노출"
      onClick={handleHide}
    />
  );
};

const ReviewListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

export const ReviewList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E', fontSize: { xs: '1.5rem', md: '2rem' } }}>
          리뷰
        </Typography>
      </Box>
      <List
        title="리뷰"
        filters={reviewFilters}
        actions={<ReviewListActions />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => (
              <Box sx={{ mb: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <ReferenceField source="productId" reference="products" link={false}>
                    <FunctionField render={(productRecord: any) => (
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, flex: 1, mr: 1 }}>
                        {productRecord?.name || '상품 정보 없음'}
                      </Typography>
                    )} />
                  </ReferenceField>
                  <StatusField source="status" />
                </Box>
              </Box>
            )}
            secondaryText={(record) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={record.rating} readOnly size="small" sx={{ fontSize: '1rem' }} />
                  <ReferenceField source="userId" reference="users" link={false}>
                    <FunctionField render={(userRecord: any) => (
                      <Typography variant="caption" color="text.secondary">
                        {userRecord?.name || userRecord?.email || '작성자 미상'}
                      </Typography>
                    )} />
                  </ReferenceField>
                </Box>
                <Typography variant="body2" sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {record.content}
                </Typography>
              </Box>
            )}
            tertiaryText={(record) => (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(record.createdAt).toLocaleDateString()}
                </Typography>
                {record.images?.length > 0 && (
                  <Chip
                    icon={<ImageIcon sx={{ fontSize: '0.9rem !important' }} />}
                    label={`${record.images.length}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                  />
                )}
              </Box>
            )}
            linkType="show"
            sx={{
              '& .MuiListItem-root': {
                padding: '16px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: 'white',
              },
              '& .MuiListItemText-primary': {
                marginBottom: 0,
              },
              '& .MuiListItemText-secondary': {
                marginTop: 0,
              }
            }}
          />
        ) : (
          <Datagrid
            rowClick="show"
            bulkActionButtons={
              <>
                <BulkShowButton />
                <BulkHideButton />
                <BulkDeleteButton />
              </>
            }
          >
            <ReferenceField source="productId" reference="products" label="상품">
              <FunctionField render={(record: any) => (
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {record.name}
                  </Typography>
                </Box>
              )} />
            </ReferenceField>
            <ReferenceField source="userId" reference="users" label="작성자">
              <FunctionField render={(record: any) => (
                <Box>
                  <Typography variant="body2">{record.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {record.email}
                  </Typography>
                </Box>
              )} />
            </ReferenceField>
            <FunctionField
              label="평점"
              render={(record: any) => (
                <RatingField source="rating" />
              )}
            />
            <FunctionField
              label="내용"
              render={(record: any) => (
                <Box sx={{ maxWidth: 300 }}>
                  <Typography variant="body2" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {record.content}
                  </Typography>
                  {record.images?.length > 0 && (
                    <Chip
                      icon={<ImageIcon />}
                      label={`사진 ${record.images.length}장`}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              )}
            />
            <FunctionField
              label="상태"
              render={(record: any) => (
                <StatusField source="status" />
              )}
            />
            <DateField source="createdAt" label="작성일" showTime />
            <ShowButton label="보기" />
            <DeleteButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// 노출/미노출 상태 변경 버튼 컴포넌트
const ToggleStatusButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const isApproved = record.status === 'APPROVED' || !record.status;
  const newStatus = isApproved ? 'HIDDEN' : 'APPROVED';

  const handleToggle = async () => {
    try {
      await dataProvider.update('reviews', {
        id: record.id,
        data: { status: newStatus },
        previousData: record,
      });
      notify(
        newStatus === 'APPROVED' ? '리뷰가 노출 상태로 변경되었습니다' : '리뷰가 미노출 상태로 변경되었습니다',
        { type: 'success' }
      );
      refresh();
    } catch (error) {
      notify('상태 변경 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  return (
    <MuiButton
      variant="contained"
      color={isApproved ? 'error' : 'success'}
      startIcon={isApproved ? <VisibilityOffIcon /> : <VisibilityIcon />}
      onClick={handleToggle}
      fullWidth
      sx={{ mt: 1 }}
    >
      {isApproved ? '미노출로 변경' : '노출로 변경'}
    </MuiButton>
  );
};

// 리뷰 제목을 상품명으로 표시하는 컴포넌트
const ReviewTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <span>
      리뷰 상세
    </span>
  );
};

export const ReviewShow = () => (
  <Show title={<ReviewTitle />}>
    <SimpleShowLayout sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>리뷰 상세 정보</Typography>
              <Box sx={{ mb: 2 }}>
                <ReferenceField source="productId" reference="products" label="상품">
                  <FunctionField render={(record: any) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {record.thumbnail && (
                        <Box
                          component="img"
                          src={record.thumbnail}
                          alt={record.name}
                          sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {record.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.price?.toLocaleString()}원
                        </Typography>
                      </Box>
                    </Box>
                  )} />
                </ReferenceField>
              </Box>
              <Box sx={{ mb: 2 }}>
                <ReferenceField source="userId" reference="users" label="작성자">
                  <FunctionField render={(record: any) => (
                    <Box>
                      <Typography variant="body1">{record.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.email} | {record.phone}
                      </Typography>
                    </Box>
                  )} />
                </ReferenceField>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>평점</Typography>
                <RatingField source="rating" />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>리뷰 내용</Typography>
                <RichTextField source="content" />
              </Box>
              {/* 리뷰 이미지 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>첨부 이미지</Typography>
                <ReviewImages />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>리뷰 상태 관리</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>현재 상태</Typography>
                <StatusField source="status" />
              </Box>
              <ToggleStatusButton />
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>작성 시간</Typography>
              <DateField source="createdAt" showTime />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </SimpleShowLayout>
  </Show>
);

// ReviewEdit 컴포넌트는 제거됨 - 고객 리뷰 내용은 함부로 편집하면 안 됨
// 상태 변경은 ReviewShow에서 ToggleStatusButton을 통해 가능